import { FC, type ReactElement } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from 'recharts';
import { fmtCompacto } from '@/utils/painelFormat';
import { margemDoRotuloEmPe, type MedidasRotulo } from '@/utils/rotulosGrafico';
import AreaDoGrafico from '../AreaDoGrafico';
import { ALTURA_GRAFICO_REPETIDO, FONTE_ROTULO, tickEixo, type CoresPainel } from '../chartTheme';
import { rotuloDoPonto } from '../rotuloBarra';

export interface ColunaDia {
  data: string;
  programado: number;
  produzido: number;
  /** Dia aberto ou por vir: a coluna do produzido fica clara. */
  esmaecido: boolean;
}

interface Props {
  pontos: ColunaDia[];
  cores: CoresPainel;
  tooltip: (props: TooltipContentProps) => ReactElement | null;
  /** O número em cima de cada coluna (a escala sai do `escalaDoDia`). */
  formatar: (v: number) => string;
  altura?: number;
}

const OPACIDADE_ABERTO = 0.45;

/**
 * 30 dias: o número deitado ("273") precisa de ~30 px por dia, em pé de ~18 (03/11/2026). Num
 * notebook de 1280 o gráfico tem ~620 px: em pé. No celular (~270 px), rola para o lado.
 */
const MEDIDAS: MedidasRotulo = { reservado: 64, deitado: 30, emPe: 18 };

/**
 * Produzido × programado dia a dia, numa coluna só por dia (26/10/2026): o produzido é a coluna
 * cheia e o programado (ou a meta) é o contorno tracejado da mesma largura, atrás dela — o que
 * sobra do contorno em cima é o que faltou produzir. Com uma coluna por dia, o número do
 * produzido cabe; com duas lado a lado, encavalava.
 *
 * Duas réguas X com o mesmo dia: o recharts põe barras da mesma régua lado a lado, e em réguas
 * diferentes uma sobre a outra. Uma linha invisível no maior dos dois valores posiciona o número
 * acima do que for mais alto, contorno ou coluna.
 */
const ColunasDoDia: FC<Props> = ({ pontos, cores, tooltip, formatar, altura = ALTURA_GRAFICO_REPETIDO }) => {
  const dados = pontos.map((p) => ({ ...p, topo: Math.max(p.programado, p.produzido) }));
  const rotulo = (_v: number, i: number) => formatar(dados[i]?.produzido ?? 0);
  const topoEmPe = margemDoRotuloEmPe(dados.map((d) => formatar(d.produzido)), FONTE_ROTULO, 20);
  return (
    <AreaDoGrafico altura={altura} categorias={dados.length} medidas={MEDIDAS}>
      {(modo) => (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={dados} margin={{ top: modo === 'em-pe' ? topoEmPe : 20, right: 4, bottom: 0, left: 0 }} barCategoryGap="14%">
            <CartesianGrid vertical={false} stroke={cores.grade} xAxisId="dia" />
            <XAxis
              xAxisId="dia"
              dataKey="data"
              tickFormatter={(d: string) => d.slice(8)}
              interval="preserveStartEnd"
              minTickGap={4}
              tick={tickEixo(cores)}
              axisLine={false}
              tickLine={false}
            />
            {/* `height={0}`: escondida, a régua do contorno ainda reservava 30 px embaixo e empurrava os
                dias da régua visível para fora do gráfico (03/11/2026). */}
            <XAxis xAxisId="alvo" dataKey="data" hide height={0} />
            <YAxis width={60} tickFormatter={fmtCompacto} tick={tickEixo(cores)} axisLine={false} tickLine={false} />
            <Tooltip content={tooltip} cursor={{ fill: cores.grade, fillOpacity: 0.35 }} />
            <Bar
              xAxisId="alvo"
              dataKey="programado"
              fill="none"
              stroke={cores.programado}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              isAnimationActive={false}
            />
            <Bar xAxisId="dia" dataKey="produzido" fill={cores.produzido} radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {dados.map((p) => (
                <Cell key={p.data} fillOpacity={p.esmaecido ? OPACIDADE_ABERTO : 1} />
              ))}
            </Bar>
            <Line
              xAxisId="dia"
              dataKey="topo"
              stroke="none"
              dot={false}
              activeDot={false}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
            >
              <LabelList dataKey="topo" content={rotuloDoPonto(rotulo, cores.texto, () => modo !== null, { emPe: modo === 'em-pe' })} />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </AreaDoGrafico>
  );
};

export default ColunasDoDia;
